'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Modal from 'react-modal';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Navigation,
  Mousewheel,
  Thumbs,
  Keyboard,
  FreeMode,
  Virtual,
} from 'swiper/modules';
import { mediaQueries } from '../../common/styleguide';
import { IoCloseOutline } from 'react-icons/io5';
import { IoChevronForward, IoChevronBack } from 'react-icons/io5';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import 'swiper/css/mousewheel';
import 'swiper/css/free-mode';
import 'swiper/css/thumbs';
import { destroyAdsGallery, requestAdsGallery } from '../ads/AdHandlerService';

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  initialSlide: number;
  images: [];
  title: string;
  id: string;
};

function GalleryModal(props: Props) {
  const { images, isOpen, onRequestClose, initialSlide, title, id } = props;
  const [isMobile, setIsMobile] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const router = useRouter();

  useEffect(() => {
    const hashChangeListener = () => {
      if (window.location.hash !== '#galerie') {
        onRequestClose();
        requestAdsGallery();
      }
    };

    window.addEventListener('hashchange', hashChangeListener);
    return () => {
      window.removeEventListener('hashchange', hashChangeListener);
    };
  }, [onRequestClose]);

  useEffect(() => {
    if (isOpen) {
      window.location.hash = '#galerie';
      window.dataLayer?.push({
        event: 'custom_event',
        event_name: 'gallery_fullscreen',
        gallery_fullscreen: {
          gallery_title: title,
          gallery_id: id,
        },
      });
      destroyAdsGallery();
    }
  }, [id, isOpen, title]);

  useEffect(() => {
    const mql = window.matchMedia(`(${mediaQueries.minWidthDesktop})`);
    const isMobile = !mql.matches;
    setIsMobile(isMobile);
  }, []);

  const slideChanged = useCallback(
    (swiper) => {
      window.dataLayer?.push({
        event: 'custom_event',
        event_name: 'gallery_swipe',
        gallery_swipe: {
          gallery_title: title,
          gallery_id: id,
          total_slides: swiper.slidesGrid.length + '',
          slide_number: swiper.activeIndex + '',
        },
      });
    },
    [id, title],
  );

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Bilder-Gallerie"
      className="fixed top-0 left-0 right-0 bottom-0 z-[10100] bg-white overflow-hidden"
      overlayClassName="zIndex-[10100]"
      onRequestClose={() => {
        router.back();
        requestAdsGallery();
      }}
      shouldCloseOnEsc
    >
      <div className="galleryContainer bg-white dark:bg-black relative w-full h-full">
        <Swiper
          modules={[
            Navigation,
            FreeMode,
            Mousewheel,
            Thumbs,
            Keyboard,
            Virtual,
          ]}
          thumbs={{ swiper: thumbsSwiper }}
          virtual
          mousewheel
          keyboard
          grabCursor
          spaceBetween={10}
          onSlideChange={slideChanged}
          navigation={{ nextEl: '.next', prevEl: '.prev' }}
          initialSlide={initialSlide}
          className="swiper-container gallery-top sm:h-[80%] w-full h-full relative"
        >
          {images &&
            images.map((image, i) => (
              <SwiperSlide key={`gallerie-${image.gallery1024.url}`}>
                <div className="w-full h-full relative">
                  <img
                    className="h-[100%] w-[100%] object-contain block"
                    width={image.gallery1024.width}
                    height={image.gallery1024.height}
                    src={image.gallery1024.url}
                    alt={image.caption}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute bottom-0 w-full bg-white/30 dark:bg-black/30 backdrop-blur-md p-3">
                    <p className="text-center dark:text-white">
                      {image.caption}
                    </p>
                    <span className="attribution dark:text-grey w-full text-center text-xxs text-black">
                      {image.attribution}
                    </span>
                  </div>
                </div>
              </SwiperSlide>
            ))}
        </Swiper>
        <button
          title="Gehe nach links"
          className="prev left-s sm:left-xxxl absolute top-[50%] z-[120] translate-y-[-50%] cursor-pointer flex justify-center items-center rounded-full w-11 h-11  transition-opacity active:bg-secundaryColor sm:hover:bg-secundaryColor bg-tintColor"
        >
          <IoChevronBack className="text-white w-6 h-6 mr-[2px]" />
        </button>
        <button
          title="Gehe nach rechts"
          className="next right-s sm:right-xleft-xxxl absolute top-[50%] z-[120] translate-y-[-50%] cursor-pointer flex justify-center items-center rounded-full w-11 h-11  transition-opacity active:bg-secundaryColor sm:hover:bg-secundaryColor bg-tintColor"
        >
          <IoChevronForward className="text-white w-6 h-6 ml-[2px]" />
        </button>
        {!isMobile && (
          <Swiper
            modules={[Navigation, Mousewheel, Thumbs, FreeMode]}
            onSwiper={setThumbsSwiper}
            spaceBetween={10}
            touchRatio={0.2}
            slidesPerView={8}
            freeMode
            mousewheel
            watchSlidesProgress
            centeredSlides
            initialSlide={initialSlide}
            className="swiper-container gallery-thumbs dark:bg-darkGrey py-5 px-0 bg-lightGrey w-full h-[20%] ml-auto mr-auto"
          >
            {images &&
              images.map((image, i) => (
                <SwiperSlide
                  key={`thumb-${image.video480.url}`}
                  className="swiper-slide w-full h-full opacity-40 cursor-pointer grayscale-[0.7]"
                >
                  <img
                    className="h-[100%] w-[100%] object-contain"
                    src={image.video480.url}
                    alt={image.caption}
                    loading="lazy"
                    decoding="async"
                  />
                </SwiperSlide>
              ))}
          </Swiper>
        )}
      </div>
      <div
        className="right-s top-s absolute z-[120] flex justify-center items-center cursor-pointer h-[44px] w-[44px] bg-white rounded-full dark:bg-black"
        onClick={() => {
          router.back();
          window.dataLayer?.push({
            event: 'custom_event',
            event_name: 'gallery_close',
            gallery_close: {
              gallery_title: title,
              gallery_id: id,
            },
          });
          requestAdsGallery();
        }}
      >
        <IoCloseOutline
          title="Gallerie Schließen"
          className="text-primaryColor w-7 h-7 active:scale-110 active:text-secundaryColor sm:hover:text-secundaryColor"
        />
      </div>
    </Modal>
  );
}

export default GalleryModal;
